package com.fireavert.common

/**
 * Unwraps a Try containing a List.
 * If the Try is a Success, it returns the list.
 * If the Try is an Error, it executes the optional [onError] block and returns an empty list.
 */
fun <T> Try<List<T>>.getOrEmptyList(onError: (Exception) -> Unit = {}): List<T> {
    // You need to explicitly reference Try.Success and Try.Error if you import Try itself
    return when (this) {
        is Try.Success -> this.value
        is Try.Error -> {
            onError(this.exception) // Perform side-effect, like logging
            emptyList()
        }
    }
}

/**
 * Unwraps a Try containing a nullable value.
 * If the Try is a Success and the value is not null, it returns the value.
 * If the Try is an Error or the value is null, it executes the optional [onError] block and returns null.
 */
fun <T> Try<T?>.getOrNull(onError: (Exception) -> Unit = {}): T? {
    return when (this) {
        is Try.Success -> this.value ?: run {
            onError(NullPointerException("Value is null"))
            null
        }

        is Try.Error -> {
            onError(this.exception) // Perform side-effect, like logging
            null
        }
    }
}

/**
 * Unwraps a Try, returning its value if it's a Success.
 * If it's an Error, it executes the optional [onError] block and then re-throws the exception.
 */
fun <T> Try<T>.getTry(onError: (Exception) -> T): T {
    return when (this) {
        is Try.Success -> this.value
        is Try.Error -> {
            "Failed to unwrap Try in ${this::class.simpleName}: ${this.exception.message}"
            onError(this.exception)
        }
    }
}
